<div class="ai-studio">
  <div class="studio-header">
    <mat-icon class="studio-icon">auto_awesome</mat-icon>
    <h2 class="studio-title">AI Form Studio</h2>
    <span class="form-context" *ngIf="data.existingFormContext?.formName">{{ data.existingFormContext?.formName }}</span>
    <span class="spacer"></span>
    <button mat-icon-button (click)="cancel()" aria-label="Close"><mat-icon>close</mat-icon></button>
  </div>

  <div class="studio-body">
    <!-- LEFT: conversational editor -->
    <div class="chat-pane">
      <div class="chat-scroll">
        <!-- Chat entry intro -->
        <div *ngIf="showChatIntro" class="intro">
          <p class="help-text">Describe what you want to add, change, or remove. Each response updates the live preview on the right. Keep chatting to refine, then Accept when you're happy.</p>

          <div *ngIf="existingParagraphs.length > 0" class="form-summary">
            <div class="form-summary-header">
              <mat-icon class="summary-icon">article</mat-icon>
              <span class="summary-title">Current form — {{ existingParagraphs.length }} paragraph{{ existingParagraphs.length !== 1 ? 's' : '' }}</span>
            </div>
            <div class="summary-list">
              <div class="summary-item" *ngFor="let p of existingParagraphs">
                <span class="summary-index">{{ p.index + 1 }}.</span>
                <span class="summary-name">{{ p.paragraphName }}</span>
                <span class="summary-count">{{ p.itemLabels.length }} item{{ p.itemLabels.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>
          </div>

          <div *ngIf="existingParagraphs.length === 0" class="empty-form-notice">
            <mat-icon>info_outline</mat-icon>
            <span>This form has no paragraphs yet. Describe the form and the AI will build it.</span>
          </div>

          <label class="prompt-label">Note Builder form type <span class="required-symbol" aria-hidden="true">*</span></label>
          <p class="help-text">
            Note Builder turns clinical answers into an assembled note. Choose the matching history
            type, or Generic for a non-clinical form. A form type is required to continue.
          </p>
          <mat-form-field appearance="outline" class="prompt-field">
            <mat-select [(ngModel)]="noteBuilderFormType" placeholder="Select a form type">
              <mat-option value="pain">Pain History</mat-option>
              <mat-option value="medical">Medical History</mat-option>
              <mat-option value="medication">Medication History</mat-option>
              <mat-option value="generic">Generic (not a clinical history form)</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Document upload card (pdf entry, before first import) — PDF or Word (.docx) -->
        <div *ngIf="showPdfCard" class="pdf-card">
          <p class="help-text">Upload a paper form as a PDF or Word document (.docx). The AI recreates it as a digital form, then you can refine it by chatting. Do not upload files containing patient data.</p>

          <div class="pdf-limit-note">
            <mat-icon>info_outline</mat-icon>
            <span>
              <strong>Limits: {{ maxFileMb }} MB and {{ maxPdfPages }} pages.</strong>
              A longer document still imports, but only its first {{ maxPdfPages }} pages are read — the
              rest are skipped. To bring in a longer document, split it and import the remaining
              pages as a second form.
            </span>
          </div>

          <div *ngIf="data.autoVersion" class="auto-version-banner">
            <mat-icon>info_outline</mat-icon>
            <span>A new draft version{{ data.autoVersionLabel ? ' (' + data.autoVersionLabel + ')' : '' }} will be created for you automatically when you accept — you don't need to create a version yourself. The imported form will be saved into it and opened in the editor.</span>
          </div>

          <div class="pdf-upload-section">
            <button mat-stroked-button type="button" (click)="pdfInput.click()">
              <mat-icon>upload_file</mat-icon>
              Choose File
            </button>
            <input #pdfInput type="file" [accept]="acceptedFileTypes" style="display:none" (change)="onFileSelected($event)">
            <span *ngIf="selectedFile" class="pdf-filename">
              {{ selectedFile.name }}
              <button mat-icon-button type="button" (click)="clearFile()" aria-label="Remove file">
                <mat-icon>close</mat-icon>
              </button>
            </span>
            <span *ngIf="!selectedFile" class="pdf-placeholder">No file selected</span>
          </div>

          <label class="prompt-label">Additional instructions (optional)</label>
          <mat-form-field appearance="outline" class="prompt-field">
            <textarea
              matInput
              rows="2"
              [(ngModel)]="instructions"
              [disabled]="isLoading"
              (keydown.enter)="$event.preventDefault(); importPdf()"
              placeholder="Example: &quot;Use two-line layout for all paragraphs&quot;  (Enter to import, Shift+Enter for a new line)">
            </textarea>
          </mat-form-field>

          <label class="prompt-label">Note Builder form type <span class="required-symbol" aria-hidden="true">*</span></label>
          <p class="help-text">
            Note Builder turns clinical answers into an assembled note. Choose the matching history
            type, or Generic for a non-clinical form. A form type is required to continue.
          </p>
          <mat-form-field appearance="outline" class="prompt-field">
            <mat-select [(ngModel)]="noteBuilderFormType" placeholder="Select a form type">
              <mat-option value="pain">Pain History</mat-option>
              <mat-option value="medical">Medical History</mat-option>
              <mat-option value="medication">Medication History</mat-option>
              <mat-option value="generic">Generic (not a clinical history form)</mat-option>
            </mat-select>
          </mat-form-field>

          <button mat-raised-button color="primary" class="import-btn" (click)="importPdf()" [disabled]="isLoading || !selectedFile || !noteBuilderFormType">
            <mat-icon>auto_awesome</mat-icon>
            Import Document
          </button>
        </div>

        <!-- Transcript -->
        <div class="chat-transcript" *ngIf="turns.length > 0">
          <div class="chat-turn" *ngFor="let turn of turns"
               [class.user-turn]="turn.role === 'user'"
               [class.assistant-turn]="turn.role === 'assistant'">
            <div class="chat-bubble">{{ turn.text }}</div>
          </div>
        </div>

        <div *ngIf="isLoading" class="progress-section">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="progress-status">
            <span class="progress-message">{{ progressMessage }}</span>
            <span class="elapsed-time">{{ elapsedSeconds }}s</span>
          </div>
          <button mat-stroked-button type="button" class="stop-btn" (click)="stopGenerating()">
            <mat-icon>stop_circle</mat-icon>
            Stop generating
          </button>
        </div>

        <!-- Review notes for the last turn: what the server adjusted or could not honour. Persists
             until the next turn rather than flashing like a snackbar — a silently partial import
             (e.g. a PDF truncated at 20 pages) has to stay readable while the user inspects the
             preview. The transcript also gets a "Heads up" turn pointing here. -->
        <div *ngIf="hasReviewNotes" class="review-notes">
          <button type="button" class="review-notes-header" (click)="toggleReviewNotes()"
            [attr.aria-expanded]="reviewNotesExpanded">
            <mat-icon class="review-notes-icon">warning_amber</mat-icon>
            <span class="review-notes-title">
              Review notes ({{ reviewNoteCount }})
            </span>
            <mat-icon class="review-notes-chevron">{{ reviewNotesExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>

          <div class="review-notes-body" *ngIf="reviewNotesExpanded">
            <ul class="review-notes-list" *ngIf="reviewWarnings.length">
              <li *ngFor="let warning of reviewWarnings">{{ warning }}</li>
            </ul>

            <div class="review-notes-unmapped" *ngIf="reviewUnmappedTerms.length">
              <span class="review-notes-subtitle">Couldn't be matched to a form control or option:</span>
              <ul class="review-notes-list">
                <li *ngFor="let term of reviewUnmappedTerms">{{ term }}</li>
              </ul>
            </div>
          </div>
        </div>

        <div *ngIf="noticeMessage" class="notice-message">{{ noticeMessage }}</div>
        <div *ngIf="errorMessage" class="error-message">{{ errorMessage }}</div>
      </div>

      <!-- Composer.
           (keydown.enter) matches ONLY an unmodified Enter — Angular's KeyEventsPlugin folds the
           held modifiers into the event name, so Shift+Enter never reaches this handler and falls
           through to the textarea's own newline. That is why there is no shiftKey check here. -->
      <div class="composer" *ngIf="composerVisible">
        <mat-form-field appearance="outline" class="prompt-field">
          <textarea
            matInput
            rows="2"
            [(ngModel)]="prompt"
            [disabled]="isLoading"
            (keydown.enter)="$event.preventDefault(); sendMessage()"
            [placeholder]="hasContent
              ? 'Refine the form…  (Enter to send, Shift+Enter for a new line)'
              : 'Describe the form to build…  (Enter to send, Shift+Enter for a new line)'">
          </textarea>
        </mat-form-field>
        <button mat-raised-button color="primary" class="send-btn" (click)="sendMessage()" [disabled]="isLoading || (formTypeRequired && !noteBuilderFormType)">
          <mat-icon>auto_awesome</mat-icon>
          {{ hasContent ? 'Send' : 'Generate' }}
        </button>
      </div>
    </div>

    <!-- RIGHT: live preview -->
    <div class="preview-pane">
      <div class="changed-strip" [class.visible]="highlightActive">
        <mat-icon>auto_awesome</mat-icon>
        <span *ngIf="changedParagraphNames.length" class="changed-names">Updated: {{ changedParagraphNames.join(', ') }}</span>
        <span *ngIf="deletedCount" class="changed-removed">· Removed {{ deletedCount }} paragraph{{ deletedCount !== 1 ? 's' : '' }}</span>
      </div>

      <div class="preview-host" [class.generating]="isLoading">
        <app-forms-builder-polished-preview
          *ngIf="previewVersion && hasContent"
          [formVersion]="previewVersion"
          [embedded]="true">
        </app-forms-builder-polished-preview>

        <div class="empty-preview" *ngIf="!hasContent && !isLoading">
          <mat-icon>description</mat-icon>
          <p>Your form preview will appear here as the AI builds it.</p>
        </div>

        <div class="generating-overlay" *ngIf="isLoading">
          <mat-spinner diameter="40"></mat-spinner>
          <span>{{ progressMessage }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="studio-actions">
    <button mat-raised-button (click)="cancel()">Cancel</button>
    <span class="spacer"></span>
    <button mat-raised-button (click)="undoLast()" [disabled]="!canUndo">
      <mat-icon>undo</mat-icon>
      Undo last
    </button>
    <button mat-raised-button color="accent" (click)="accept()" [disabled]="isLoading || !hasContent">
      <mat-icon>check</mat-icon>
      {{ data.autoVersion ? 'Create Version & Open Editor' : 'Accept' }}
    </button>
  </div>
</div>
